import L from 'leaflet';
import PropTypes from 'prop-types';
import { useRef } from 'react';

import useLeafletLayer from './useLeafletLayer';

const TILE_PX = 256;
const NO_SHIFT = {
  x: 0,
  y: 0,
};

//
// Tiles of one page of a drawing, rendered on demand by a PlanRenderer, for the maps the plan
// editor shows before any tile is uploaded. The map's CRS is planMapCrs() with its defaults, so
// a tile at zoom z covers 256 / 2^z crop units and renders at 2^z pixels per unit; the map is
// in crop coordinates, which the page draws at its offset.
//
const LiveGridLayer = L.GridLayer.extend({
  createTile(coords, done) {
    const tile = document.createElement('canvas');
    tile.width = TILE_PX;
    tile.height = TILE_PX;

    const {
      renderer,
      page,
      pageOffset,
    } = this.options;
    const scale = 2 ** coords.z;
    const ticket = renderer.render({
      page,
      scale,
      left: (coords.x * TILE_PX) + (pageOffset.x * scale),
      top: (coords.y * TILE_PX) + (pageOffset.y * scale),
      width: TILE_PX,
      height: TILE_PX,
    });
    tile.pxTicket = ticket;

    ticket.promise.then(
      (bitmap) => {
        const context = tile.getContext('2d');
        context.drawImage(bitmap, 0, 0);
        bitmap.close();
        // Only the crop's rectangle shows: the rest of the page is cleared from the tile
        const { rect } = this.options;
        if (rect) {
          context.globalCompositeOperation = 'destination-in';
          context.fillRect(
            (rect.x * scale) - (coords.x * TILE_PX),
            (rect.y * scale) - (coords.y * TILE_PX),
            rect.width * scale,
            rect.height * scale,
          );
          context.globalCompositeOperation = 'source-over';
        }
        done(null, tile);
      },
      (err) => {
        if (err.message !== 'abandoned') done(err, tile);
      },
    );
    return tile;
  },
});

function LeafletLivePlanLayer(props) {
  const {
    renderer,
    page,
    rect,
    pageOffset = NO_SHIFT,
    minZoom,
    maxZoom,
  } = props;
  const propsRef = useRef({
    renderer,
    page,
    rect,
    pageOffset,
    minZoom,
    maxZoom,
  });

  useLeafletLayer(
    () => {
      // A GridLayer hides itself below its own minZoom (0 by default): a whole page fits the
      // pane only at negative zooms, so the layer follows the map's range
      const layer = new LiveGridLayer({
        renderer: propsRef.current.renderer,
        page: propsRef.current.page,
        rect: propsRef.current.rect,
        pageOffset: propsRef.current.pageOffset,
        minZoom: propsRef.current.minZoom,
        maxZoom: propsRef.current.maxZoom,
        tileSize: TILE_PX,
        updateWhenZooming: false,
        keepBuffer: 1,
      });
      // A tile leaving the view before its turn is dropped from the render queue
      layer.on('tileunload', ({ tile }) => tile.pxTicket?.abandon());
      return layer;
    },
  );

  return null;
}

LeafletLivePlanLayer.propTypes = {
  renderer: PropTypes.shape({ render: PropTypes.func.isRequired }).isRequired,
  page: PropTypes.number.isRequired,
  // In crop coordinates; without it the whole page shows
  rect: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }),
  // Where the page draws the crop's coordinates, in page units (cropPageOffset)
  pageOffset: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }),
  minZoom: PropTypes.number.isRequired,
  maxZoom: PropTypes.number.isRequired,
};

export default LeafletLivePlanLayer;
